// Wisconsin DHS F-11133 Personal Care Screening Tool — state-mandated rule engine.
// Source: F-11133A (11/2024) Instructions. All rules below are direct, literal
// enforcements of DHS policy, not clinical judgments.
//
// Rule keys (used in stepValidation + UI red-rings):
//   DHS-C-COMMENT  — Level "C" or greater requires a comment describing PCW actions.
//   DHS-AGE-GATE   — Age-appropriate code may only be selected when the member's
//                    age is within the allowed range for that ADL.
//   DHS-TUBE-LINK  — Eating level "0" (tube/IV fed) requires a Part III tube feeding
//                    selection so the PA reviewer can tie feeding time to the MOT.
//   DHS-LIFT-LINK  — Transferring level "F" (mechanical lift) requires Part III
//                    "Other" to be populated per Element 26 instructions.
//   DHS-SCREEN-DATE — Screen completion date must be no more than 90 days before
//                     the requested PA start date (SOC / cert-from).
//   DHS-PRN-FORMAT  — PRN time description must use the exact DHS boilerplate.
//   DHS-PRN-MED     — PRN medication assistance is NOT a covered service.
//   DHS-SEIZURE     — Only grand-mal (tonic-clonic) seizures occurring >=1/week
//                     with interventions qualify.
//   DHS-BEHAVIOR    — Behavior "Yes" requires a concrete description (aggression /
//                     physical resistance). Redirection time is not covered.
//   DHS-RARE        — Rare-condition "Yes" requires naming the condition AND the
//                     member-specific precaution or equipment the PCW follows.
//   DHS-PARAM       — Glucometer / Vital Signs in Part II require PCP parameters
//                     and interventions on the POC (flag to RN).
//
// Validators below emit {severity, rule, field, label, testid, message}.

// Age-appropriate gate thresholds per element (DHS F-11133). The `code` is ALSO
// exempt from the C-or-greater comment rule because it indicates "normal for age",
// not a level-of-assistance finding.
const AGE_GATES = {
  bathing:      { code: "F", maxYears: 5 },
  dressing:     { code: "F", maxYears: 5 },
  grooming:     { code: "G", maxYears: 5 },
  eating:       { code: "H", maxYears: 3 },
  mobility:     { code: "E", maxYears: 1.5 },
  toileting:    { code: "G", maxYears: 4 },
  transferring: { code: "G", maxYears: 3 },
};

// Per-ADL, any selected letter in this set is COMMENT-REQUIRED per DHS. The set
// is "C or greater" minus the age-appropriate code for that ADL.
function commentRequiredLetters(adlKey) {
  const all = new Set(["C", "D", "E", "F", "G"]);
  const ageCode = AGE_GATES[adlKey]?.code;
  if (ageCode) all.delete(ageCode);
  return all;
}

export function requiresComment(level, adlKey) {
  const v = (level || "").toString().trim().toUpperCase();
  if (!v) return false;
  if (adlKey) return commentRequiredLetters(adlKey).has(v);
  // Fallback: generic C-or-greater (used by UI when ADL key not available).
  return ["C", "D", "E", "F", "G"].includes(v);
}

function ageInYears(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  // Include months for fractional threshold comparisons
  const fractional = years + (m + (m < 0 ? 12 : 0)) / 12;
  return { whole: years, fractional };
}

// PRN DHS-mandated verbatim phrasing (Element 34). RN may append specifics.
export const PRN_TEMPLATE =
  "24 hours/year for acute exacerbations of illness or attending medical appointments to assist with ADLs";

function prnMatchesTemplate(text) {
  if (!text) return false;
  const normalized = text.toLowerCase().replace(/\s+/g, " ").trim();
  return normalized.includes("24 hours/year")
      && normalized.includes("acute exacerbations")
      && normalized.includes("adl");
}

// Days within threshold (inclusive).
function daysBetween(dateA, dateB) {
  const a = new Date(dateA);
  const b = new Date(dateB);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// Validates the entire ADL block (Step 2).
export function adlDhsIssues(data) {
  const issues = [];
  const adl = data.adl || {};
  const age = ageInYears(data?.client?.date_of_birth);

  Object.entries(AGE_GATES).forEach(([key, gate]) => {
    const row = adl[key] || {};
    const level = (row.pcst_level || "").toString().toUpperCase();

    // DHS-C-COMMENT: C+ requires comment, except when the selected letter is the
    // age-appropriate gate code (which means "normal for age", not a help level).
    if (requiresComment(level, key) && !(row.notes || "").trim()) {
      issues.push({
        severity: "error",
        rule: "DHS-C-COMMENT",
        field: `adl.${key}.notes`,
        label: `${key[0].toUpperCase()}${key.slice(1)}: Level ${level} requires a comment describing PCW actions / DME`,
        testid: `adl-${key}-notes`,
        message: "DHS F-11133 requires comments any time 'C' or greater is selected — describe functional impairment, PCW intervention, and DME used.",
      });
    }

    // DHS-AGE-GATE: age-appropriate letter selected but member is older
    if (level === gate.code && age && age.fractional > gate.maxYears) {
      issues.push({
        severity: "error",
        rule: "DHS-AGE-GATE",
        field: `adl.${key}.pcst_level`,
        label: `${key}: Level "${gate.code}" is reserved for children ≤ ${gate.maxYears} yrs`,
        testid: `adl-${key}-pcst-level`,
        message: `Member is ${age.whole} years old. Level "${gate.code}" is only valid for a child aged ${gate.maxYears} or younger. Select a different level or add a written justification.`,
      });
    }
  });

  // DHS-TUBE-LINK: eating = "0" requires a Part III tube feeding row
  const eatingLevel = (adl.eating?.pcst_level || "").toString();
  if (eatingLevel === "0") {
    const p3 = data?.nursing?.part_iii || {};
    const hasTube = p3.tube_continuous?.selected || p3.tube_bolus?.selected;
    if (!hasTube) {
      issues.push({
        severity: "error",
        rule: "DHS-TUBE-LINK",
        field: "nursing.part_iii.tube_bolus",
        label: "Tube-fed eating (Level 0) requires a Part III tube feeding selection",
        testid: "mot-p3-tube_bolus",
        message: "DHS Element 23 requires that Level 0 (tube/IV fed) also check Daily Tube Feedings (continuous or bolus) in Part III.",
      });
    }
  }

  // DHS-LIFT-LINK: transferring = "F" (mechanical lift) requires Element 29 "Other"
  const transferLevel = (adl.transferring?.pcst_level || "").toString();
  if (transferLevel === "F") {
    const otherOk = (data?.nursing?.part_iii?.other_description || "").trim().length > 0
                 || (data?.nursing?.part_iii?.comments || "").toLowerCase().includes("lift");
    if (!otherOk) {
      issues.push({
        severity: "error",
        rule: "DHS-LIFT-LINK",
        field: "nursing.part_iii.other_description",
        label: "Mechanical lift transfer requires Element 29 'Other' description",
        testid: "mot-p3-comments",
        message: "DHS Element 26 requires that Level F (Hoyer/EZ-Stand mechanical lift) be documented under Part III 'Other' with the specific lift and PCW procedure.",
      });
    }
  }

  return issues;
}

// Validates nursing / considerations (Step 3).
export function nursingDhsIssues(data) {
  const issues = [];

  // DHS-PARAM: Glucometer / Vital Signs require PCP parameters on POC (informational)
  const p2 = data?.nursing?.part_ii || {};
  if (p2.glucometer?.selected) {
    issues.push({
      severity: "info",
      rule: "DHS-PARAM",
      field: "nursing.part_ii.glucometer",
      label: "Glucometer monitoring requires specific PCP parameters & interventions on the POC",
      testid: "mot-p2-glucometer",
      message: "DHS: Specify thresholds (e.g., '<70 give glucose tabs, contact PCP; >300 contact PCP') on the Plan of Care before submitting.",
    });
  }
  if ((data?.nursing?.part_iii || {}).vitals?.selected) {
    issues.push({
      severity: "info",
      rule: "DHS-PARAM",
      field: "nursing.part_iii.vitals",
      label: "Vital-signs monitoring requires PCP parameters & interventions on the POC",
      testid: "mot-p3-vitals",
      message: "DHS: Cite the specific parameter (e.g., 'SBP > 170 or DBP > 95 → contact PCP') and the member's functional impairment preventing self-measurement.",
    });
  }

  // DHS-PRN-MED: Element 27 level D + any PRN-dose medication = warn
  const medLevel = (data?.nursing?.medication_assistance?.level || "").toUpperCase();
  const hasPrnMed = (data?.medications || []).some((m) =>
    /prn/i.test((m.frequency || m.schedule || "").toString()),
  );
  if (medLevel === "D" && hasPrnMed) {
    issues.push({
      severity: "warning",
      rule: "DHS-PRN-MED",
      field: "nursing.medication_assistance.level",
      label: "PRN medication assistance is NOT a covered service",
      testid: "mot-med-level",
      message: "DHS Element 27: PCW assistance with PRN medications cannot be authorized. Remove PRN meds from the delegated list or reduce Element 27 to level B (reminders).",
    });
  }

  // DHS-PRN-FORMAT: if PRN requested, description should include the DHS boilerplate
  const prn = data?.prn || {};
  if ((prn.requested || "").toLowerCase() === "yes"
      && !prnMatchesTemplate(prn.description)) {
    issues.push({
      severity: "error",
      rule: "DHS-PRN-FORMAT",
      field: "prn.description",
      label: "PRN description must match the DHS verbatim template",
      testid: "consider-prn-desc",
      message: `DHS Element 34 mandates the phrasing: "${PRN_TEMPLATE}". Tap the "Insert DHS PRN template" button.`,
    });
  }

  // DHS-SEIZURE: only grand-mal ≥ 1/week qualifies
  const sz = data?.seizures || {};
  if ((sz.present || "").toLowerCase() === "yes") {
    const typ = (sz.type || "").toLowerCase();
    const isGrandMal = /grand mal|tonic[- ]?clonic/.test(typ);
    const isAbsence = /absence|petit mal|pseudo/.test(typ);
    if (isAbsence) {
      issues.push({
        severity: "error",
        rule: "DHS-SEIZURE",
        field: "seizures.type",
        label: "Absence / petit-mal / pseudo-seizures are NOT covered",
        testid: "consider-seizures-type",
        message: "DHS Element 33: Only grand-mal (tonic-clonic) seizures are covered. Remove the Yes flag or change the type.",
      });
    } else if (!isGrandMal) {
      issues.push({
        severity: "warning",
        rule: "DHS-SEIZURE",
        field: "seizures.type",
        label: "Seizure type must be tonic-clonic (grand mal) to qualify",
        testid: "consider-seizures-type",
        message: "DHS Element 33 only allows additional PCW time for tonic-clonic seizures. Specify the type.",
      });
    }
    if (!(sz.frequency || "").trim()) {
      issues.push({
        severity: "error",
        rule: "DHS-SEIZURE",
        field: "seizures.frequency",
        label: "Seizure frequency required (must be ≥ 1/week to qualify for PCW time)",
        testid: "consider-seizures-freq",
        message: "DHS Element 33 requires weekly-or-greater frequency and a date of last seizure.",
      });
    }
  }

  // DHS-BEHAVIOR: Yes requires concrete description
  const beh = data?.behaviors || {};
  if ((beh.present || "").toLowerCase() === "yes") {
    const desc = (beh.description || "").toLowerCase();
    if (!desc.trim()) {
      issues.push({
        severity: "error",
        rule: "DHS-BEHAVIOR",
        field: "behaviors.description",
        label: "Behaviors (Yes) requires a description of aggression / physical resistance",
        testid: "consider-behaviors-desc",
        message: "DHS Element 31 covers acts of aggression or physical resistance — not redirection or encouragement. Document specifics.",
      });
    }
  }

  // DHS-RARE: Yes requires both condition name and precaution
  const rare = data?.rare_condition || {};
  if ((rare.present || "").toLowerCase() === "yes"
      && (rare.description || "").trim().length < 20) {
    issues.push({
      severity: "warning",
      rule: "DHS-RARE",
      field: "rare_condition.description",
      label: "Rare condition requires the condition name AND PCW-specific precautions",
      testid: "consider-rare-desc",
      message: "DHS Element 32 is limited to rarely-diagnosed conditions that require protective equipment or member-specific precautions. Provide both.",
    });
  }

  return issues;
}

// Validates dates (Step 1 + Step 7).
export function dateDhsIssues(data) {
  const issues = [];
  // Element 2 — "Screen Completion Date". We map this to the existing
  // wizard field `client.assessment_date` (populated on Step 1).
  const screenDate = data?.client?.assessment_date || data?.pcst_screen_date || data?.screen_date;
  const socDate = data?.cms485?.cert_period_from;
  if (screenDate && socDate) {
    const delta = daysBetween(screenDate, socDate);
    if (delta !== null && delta > 90) {
      issues.push({
        severity: "error",
        rule: "DHS-SCREEN-DATE",
        field: "client.assessment_date",
        label: "Screen completion date must be ≤ 90 days before the requested start date",
        testid: "client-assessment-date",
        message: `DHS Element 2: In-person evaluation must be within 90 days of SOC. Gap is ${delta} days.`,
      });
    }
    if (delta !== null && delta < 0) {
      issues.push({
        severity: "warning",
        rule: "DHS-SCREEN-DATE",
        field: "client.assessment_date",
        label: "Screen date is after the requested SOC",
        testid: "client-assessment-date",
        message: "ForwardHealth will not authorize services before the in-person evaluation date.",
      });
    }
  }
  return issues;
}

// Aggregate all DHS issues for the full payload — used by the AI Review panel.
export function allDhsIssues(data) {
  return [
    ...adlDhsIssues(data),
    ...nursingDhsIssues(data),
    ...dateDhsIssues(data),
  ];
}

// Map each DHS rule to the RN checklist item it compliance-gates. An unresolved
// DHS error on a rule auto-blocks (and auto-unchecks) the matching checklist
// item, keeping the final PDF gate honest.
//
// Keys match RN_CHECKLIST_ITEMS ids in sampleData.js.
export const DHS_RULE_TO_CHECKLIST = {
  "DHS-C-COMMENT":   "adl_scored",
  "DHS-AGE-GATE":    "adl_scored",
  "DHS-TUBE-LINK":   "nursing_delegation",
  "DHS-LIFT-LINK":   "nursing_delegation",
  "DHS-PRN-MED":     "nursing_delegation",
  "DHS-SCREEN-DATE": "client_id_verified",
  "DHS-PRN-FORMAT":  "goals_measurable",
  "DHS-SEIZURE":     "diagnoses_supported",
  "DHS-BEHAVIOR":    "care_plan_reviewed",
  "DHS-RARE":        "diagnoses_supported",
  "DHS-PARAM":       "nursing_delegation",
};

// Return { [checklistId]: [issues] } for every checklist item that has at least
// one unresolved DHS ERROR (warnings/info do NOT block — they remain advisory).
export function checklistBlockers(data) {
  const issues = allDhsIssues(data).filter((i) => i.severity === "error");
  const out = {};
  for (const issue of issues) {
    const target = DHS_RULE_TO_CHECKLIST[issue.rule];
    if (!target) continue;
    if (!out[target]) out[target] = [];
    out[target].push(issue);
  }
  return out;
}
